import { Box, Button, Slider, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";

import {
  toEmittedValue,
  toSliderValue,
} from "~/components/UI/FilterRangeSlider/utils";
import type { IRangeValue } from "~/types/range";

import * as Styled from "./FilterRangeSliderForm.styles";

export interface FilterRangeSliderFormProps {
  initialValue?: IRangeValue;
  min: number;
  max: number;
  step: number;
  title?: string;
  getHelpText?: (value: [number, number]) => string;
  onApply: (value: IRangeValue) => void;
}

export const FilterRangeSliderForm: FC<FilterRangeSliderFormProps> = ({
  initialValue,
  min,
  max,
  step,
  onApply,
  title,
  getHelpText,
}) => {
  const initialSliderValue = useMemo(
    () => toSliderValue(initialValue, min, max),
    [initialValue, max, min]
  );
  const [value, setValue] = useState<[number, number]>(initialSliderValue);
  const helpText = useMemo(() => getHelpText?.(value), [getHelpText, value]);

  useEffect(() => {
    setValue(initialSliderValue);
  }, [initialSliderValue]);

  const handleChange = (_event: Event, nextValue: number | number[]) => {
    if (Array.isArray(nextValue)) {
      setValue([nextValue[0], nextValue[1]]);
    }
  };

  const handleSubmit = () => {
    onApply(toEmittedValue(value, min, max));
  };

  return (
    <Styled.FormContainer>
      {title && <Typography variant="caption">{title}</Typography>}

      <Box
        sx={{
          px: 1,
          pt: 2
        }}>
        <Slider
          size="small"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={handleChange}
          valueLabelDisplay="off"
          disableSwap
        />
      </Box>

      {helpText && (
        <Styled.HelperText variant="caption" color="text.secondary">
          {helpText}
        </Styled.HelperText>
      )}

      <Button variant="contained" size="small" onClick={handleSubmit}>
        Apply
      </Button>
    </Styled.FormContainer>
  );
};
